import { useState, useEffect, useCallback, useRef } from "react";
import roomDiscoveryService from "../services/rooms/roomDiscoveryService";

/**
 * Custom hook for Research Room Discovery
 * Handles fetching and managing discovery data
 */
export const useDiscovery = (options = {}) => {
  const { autoRefresh = false, refreshInterval = 60000 } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("forYou");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const mountedRef = useRef(true);

  // Fetch homepage data
  const fetchHomepage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomDiscoveryService.getHomepage();
      if (mountedRef.current) {
        if (response.success) {
          setData(response.data);
        } else {
          setError(response.message || "Failed to load discovery");
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message || "Failed to load discovery");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Refresh data
  const refresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await roomDiscoveryService.refreshCache();
      await fetchHomepage();
    } catch (err) { /* ignored */ } finally {
      if (mountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [fetchHomepage]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchHomepage();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchHomepage]);

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchHomepage, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchHomepage]);

  return {
    data,
    loading,
    error,
    activeTab,
    setActiveTab,
    refresh,
    isRefreshing,
    refetch: fetchHomepage,
  };
};

/**
 * Hook for infinite scroll "For You" feed
 */
export const useForYouFeed = () => {
  const [rooms, setRooms] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const response = await roomDiscoveryService.getForYou({ page });
      if (response.success) {
        const newRooms = response.data.rooms || [];
        setRooms((prev) => (page === 1 ? newRooms : [...prev, ...newRooms]));
        setHasMore(response.data.pagination?.hasMore ?? false);
        setPage((prev) => prev + 1);
      }
    } catch (err) { /* ignored */ } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [page, loading, hasMore]);

  const reset = useCallback(() => {
    setRooms([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
  }, []);

  // Initial load
  useEffect(() => {
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { rooms, loading, initialLoading, hasMore, loadMore, reset };
};

/**
 * Hook for discovery search
 */
export const useDiscoverySearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = useCallback(async (searchQuery, filters = {}) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await roomDiscoveryService.search(searchQuery, filters);
      if (response.success) {
        setResults(response.data.rooms || []);
      }
    } catch (err) {
setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setQuery("");
    setResults([]);
    setSearched(false);
  }, []);

  return {
    query,
    setQuery,
    results,
    loading,
    searched,
    search,
    clearSearch,
  };
};

export default useDiscovery;
